import { Alert, Button, MonetaryAmount } from '@components'
import type {
  DeVatSection,
  ReportRequestBody,
  VatJson,
  VatReportEntry,
  VatSummaryLine
} from '@query'
import type { TFunction } from 'i18next'
import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { FaChevronDown, FaChevronRight } from 'react-icons/fa'
import styled from 'styled-components'
import { VatLineEntriesPanel } from './VatLineEntriesPanel'
import {
  AccountCode,
  Caret,
  ColumnHeaderRow,
  LineItemHeader,
  ReportNoticeBanner,
  ReportRow,
  RowLabel,
  RowLabelText,
  RowMeta,
  ReportViewport,
  ValueCell,
  ValueHeader
} from './interactive.styled'
import { vatTableMinWidthPx } from './periodUtils'
import { ReportCardLayout } from './ReportCardLayout'
import { ReportLeadingGutter } from './ReportLeadingGutter'
import type { ReportExpansion } from './interactiveReportsSession'
import { useOnTokenIncrement } from './useOnTokenIncrement'

type Props = {
  slug: string
  reportBody: ReportRequestBody
  data: VatJson
  expansion: ReportExpansion
  onExpansionChange: (patch: Partial<ReportExpansion>) => void
  onOpenEuSalesReport: () => void
  currency?: string
  expandAllToken?: number
  collapseAllToken?: number
  isRefreshing?: boolean
  isStale?: boolean
}

type VatLine =
  | { kind: 'header'; id: string; label: string }
  | {
      kind: 'line' | 'calc'
      id: string
      label: string
      amount: number
      entries_count: number
      strong?: boolean
      code?: string
    }

const asNumber = (value: unknown): number | null => {
  if (typeof value === 'number') return value
  if (typeof value === 'string' && value.trim() !== '' && !Number.isNaN(Number(value))) {
    return Number(value)
  }
  return null
}

const entryCount = (line: VatSummaryLine) => line.entries_count ?? line.entries.length

/** Accepts Phase-2 `{ amount, entries }` or legacy bare numbers. */
const parseSummaryLine = (value: unknown): VatSummaryLine | null => {
  if (value == null) return null
  if (typeof value === 'number' || typeof value === 'string') {
    const amount = asNumber(value)
    return amount == null ? null : { amount, entries: [] }
  }
  if (typeof value === 'object' && 'amount' in value) {
    const record = value as {
      amount: unknown
      entries?: unknown
      entries_count?: unknown
      label?: unknown
      code?: unknown
    }
    const amount = asNumber(record.amount)
    if (amount == null) return null
    const entries = Array.isArray(record.entries) ? (record.entries as VatReportEntry[]) : []
    const entries_count =
      typeof record.entries_count === 'number' ? record.entries_count : entries.length
    return {
      amount,
      entries,
      entries_count,
      label: typeof record.label === 'string' ? record.label : undefined,
      code: typeof record.code === 'string' ? record.code : undefined
    }
  }
  return null
}

const FI_PURCHASE_TAX_ROWS = [
  ['eu_purchase_of_goods_tax', 'euPurchaseGoodsTax'],
  ['eu_purchase_of_services_tax', 'euPurchaseServicesTax'],
  ['purchase_of_goods_outside_eu_tax', 'purchaseOutsideEuTax'],
  ['purchase_of_construction_services_tax', 'purchaseConstructionTax']
] as const

const FI_SALES_PURCHASES_ROWS = [
  ['zero_tax_rate', 'zeroTaxRate'],
  ['eu_sales_of_goods', 'euSalesGoods'],
  ['eu_sales_of_services', 'euSalesServices'],
  ['eu_purchase_of_goods', 'euPurchaseGoods'],
  ['eu_purchase_of_services', 'euPurchaseServices'],
  ['purchase_of_goods_outside_eu', 'purchaseOutsideEu'],
  ['sale_of_construction_services', 'saleConstruction'],
  ['purchase_of_construction_services', 'purchaseConstruction']
] as const

const pushFiSummaryLine = (lines: VatLine[], id: string, label: string, value: unknown) => {
  const line = parseSummaryLine(value)
  if (!line) return
  lines.push({
    kind: 'line',
    id,
    label,
    amount: line.amount,
    entries_count: entryCount(line)
  })
}

const buildFiVatLines = (summary: Record<string, unknown>, t: TFunction): VatLine[] => {
  const lines: VatLine[] = []

  lines.push({
    kind: 'header',
    id: 'h-domestic',
    label: t('reports.interactive.vat.domesticSalesByRate')
  })
  const domestic = summary.domestic_sales
  if (domestic && typeof domestic === 'object') {
    Object.entries(domestic as Record<string, unknown>).forEach(([rate, value]) => {
      pushFiSummaryLine(
        lines,
        `domestic_sales:${rate}`,
        t('reports.interactive.vat.rateLabel', { rate }),
        value
      )
    })
  }

  lines.push({
    kind: 'header',
    id: 'h-purchases-tax',
    label: t('reports.interactive.vat.taxOnPurchases')
  })
  FI_PURCHASE_TAX_ROWS.forEach(([key, labelKey]) => {
    pushFiSummaryLine(lines, key, t(`reports.interactive.vat.${labelKey}`), summary[key])
  })

  const deductible = parseSummaryLine(summary.tax_receivables)
  if (deductible) {
    lines.push({
      kind: 'line',
      id: 'tax_receivables',
      label: t('reports.interactive.vat.deductibleTax'),
      amount: deductible.amount,
      entries_count: entryCount(deductible)
    })
  }

  const taxToPay = asNumber(summary.tax_to_be_paid)
  if (taxToPay != null) {
    lines.push({
      kind: 'calc',
      id: 'tax_to_be_paid',
      label: t('reports.interactive.vat.taxPayable'),
      amount: taxToPay,
      entries_count: 0,
      strong: true
    })
  }

  lines.push({
    kind: 'header',
    id: 'h-sales-purchases',
    label: t('reports.interactive.vat.salesPurchasesImports')
  })
  FI_SALES_PURCHASES_ROWS.forEach(([key, labelKey]) => {
    pushFiSummaryLine(lines, key, t(`reports.interactive.vat.${labelKey}`), summary[key])
  })

  return lines
}

const pushKennzahlLine = (
  lines: VatLine[],
  code: string,
  parsed: VatSummaryLine,
  skipZeroAmount = false
) => {
  if (skipZeroAmount && parsed.amount === 0 && code !== '83') return
  lines.push({
    kind: 'line',
    id: `map:${code}`,
    label: parsed.label || code,
    amount: parsed.amount,
    entries_count: code === '83' ? 0 : entryCount(parsed),
    code
  })
}

const pushDeSectionTotals = (
  lines: VatLine[],
  totalKeys: string[],
  totals: Record<string, number>,
  totalsLabels: Record<string, string>
) => {
  totalKeys.forEach(totalKey => {
    const amount = asNumber(totals[totalKey])
    if (amount == null) return
    lines.push({
      kind: 'calc',
      id: `total:${totalKey}`,
      label: totalsLabels[totalKey] || totalKey,
      amount,
      entries_count: 0,
      strong: totalKey === 'payable'
    })
  })
}

const buildDeVatLinesFromSections = (
  sections: DeVatSection[],
  mapping: Record<string, number | VatSummaryLine>,
  totals: Record<string, number>,
  totalsLabels: Record<string, string>
): VatLine[] => {
  const lines: VatLine[] = []

  const pushKennzahl = (target: VatLine[], code: string) => {
    const parsed = parseSummaryLine(mapping[code])
    if (!parsed) return
    pushKennzahlLine(target, code, parsed, true)
  }

  sections.forEach(section => {
    const sectionLines: VatLine[] = []
    pushDeSectionTotals(sectionLines, section.totals ?? [], totals, totalsLabels)
    section.codes.forEach(code => pushKennzahl(sectionLines, code))
    if (!sectionLines.length && !section.always_visible) return
    lines.push({
      kind: 'header',
      id: `h-${section.id}`,
      label: section.label
    })
    lines.push(...sectionLines)
  })

  return lines
}

const buildDeVatLinesFallback = (
  data: VatJson,
  t: TFunction,
  totalsLabels: Record<string, string>
): VatLine[] => {
  const lines: VatLine[] = []

  if (data.totals) {
    lines.push({
      kind: 'header',
      id: 'h-totals',
      label: t('reports.interactive.vat.totals')
    })
    Object.entries(data.totals).forEach(([key, amount]) => {
      lines.push({
        kind: 'calc',
        id: `total:${key}`,
        label: totalsLabels[key] || key,
        amount,
        entries_count: 0
      })
    })
  }

  if (data.mapping) {
    lines.push({
      kind: 'header',
      id: 'h-mapping',
      label: t('reports.interactive.vat.mapping')
    })
    Object.entries(data.mapping).forEach(([code, value]) => {
      const parsed = parseSummaryLine(value)
      if (!parsed) return
      pushKennzahlLine(lines, code, parsed)
    })
  }

  return lines
}

const buildDeVatLines = (data: VatJson, t: TFunction): VatLine[] => {
  const mapping = data.mapping ?? {}
  const totals = data.totals ?? {}
  const totalsLabels = data.totals_labels ?? {}
  const sections = data.de_sections

  if (sections?.length) {
    return buildDeVatLinesFromSections(sections, mapping, totals, totalsLabels)
  }

  return buildDeVatLinesFallback(data, t, totalsLabels)
}

const buildVatLines = (data: VatJson, t: TFunction): VatLine[] => {
  if (data.summary) return buildFiVatLines(data.summary, t)
  if (data.totals || data.mapping) return buildDeVatLines(data, t)
  return []
}

export const VatView: React.FC<Props> = ({
  slug,
  reportBody,
  data,
  expansion,
  onExpansionChange,
  onOpenEuSalesReport,
  currency,
  expandAllToken = 0,
  collapseAllToken = 0,
  isRefreshing = false,
  isStale = false
}) => {
  const [t] = useTranslation()
  const expanded = expansion.items

  const lines = useMemo(() => buildVatLines(data, t), [data, t])

  useOnTokenIncrement(expandAllToken, () => {
    const next: Record<string, boolean> = {}
    lines.forEach(line => {
      if (line.kind !== 'header' && line.entries_count > 0) {
        next[line.id] = true
      }
    })
    onExpansionChange({ items: next })
  })

  useOnTokenIncrement(collapseAllToken, () => {
    onExpansionChange({ items: {} })
  })

  return (
    <ReportViewport>
      <ReportCardLayout
        title={t('constants.reportType.vatReport')}
        isRefreshing={isRefreshing}
        isStale={isStale}
        footNote={t('reports.interactive.footNote.vat')}
        banner={
          data.recapitulative_statement_required ? (
            <ReportNoticeBanner $tone="info">
              <Alert
                type="info"
                inline
                marginBottom={0}
                description={
                  <RecapCopy>
                    <span>{t('reports.interactive.recapitulativeRequired')}</span>
                    <Button type="button" intent="default" size="sm" onClick={onOpenEuSalesReport}>
                      {t('reports.interactive.openEuSalesReport')}
                    </Button>
                  </RecapCopy>
                }
              />
            </ReportNoticeBanner>
          ) : null
        }
        tableMinWidth={vatTableMinWidthPx()}
      >
        <ColumnHeaderRow>
          {data.mapping ? (
            <AccountCode $header>{t('reports.interactive.vat.kennzahlen')}</AccountCode>
          ) : null}
          <ReportLeadingGutter variant="caret" />
          <LineItemHeader>{t('reports.interactive.vat.returnLine')}</LineItemHeader>
          <ValueHeader>{t('reports.interactive.amount')}</ValueHeader>
        </ColumnHeaderRow>

        {lines.map(line => {
          if (line.kind === 'header') {
            return (
              <ReportRow key={line.id} $strong $borderStrong>
                <AccountCode />
                <RowLabel $strong style={{ letterSpacing: '0.6px', textTransform: 'uppercase' }}>
                  <Caret />
                  <RowLabelText>{line.label}</RowLabelText>
                </RowLabel>
                <ValueCell />
              </ReportRow>
            )
          }

          const hasEntries = line.entries_count > 0
          const open = !!expanded[line.id]

          return (
            <React.Fragment key={line.id}>
              <ReportRow
                $strong={line.kind === 'calc'}
                $bg={line.kind === 'calc' || open ? '#f7fafd' : undefined}
                $borderStrong={line.kind === 'calc'}
                $clickable={hasEntries}
                onClick={() =>
                  hasEntries &&
                  onExpansionChange({
                    items: { ...expanded, [line.id]: !expanded[line.id] }
                  })
                }
              >
                <AccountCode>{line.code}</AccountCode>
                <RowLabel
                  $indentFrom={line.kind === 'line' ? 36 : 16}
                  $strong={line.kind === 'calc' || line.strong}
                >
                  <Caret>{hasEntries ? open ? <FaChevronDown /> : <FaChevronRight /> : null}</Caret>
                  <RowLabelText>
                    {line.label}
                    {hasEntries && (
                      <RowMeta>
                        {t('reports.interactive.entryCount', { count: line.entries_count })}
                      </RowMeta>
                    )}
                  </RowLabelText>
                </RowLabel>
                <ValueCell $strong={line.kind === 'calc' || line.strong}>
                  <MonetaryAmount value={line.amount} currency={currency} showColor={line.strong} />
                </ValueCell>
              </ReportRow>

              {open && hasEntries && (
                <VatLineEntriesPanel
                  slug={slug}
                  reportBody={reportBody}
                  lineId={line.id}
                  currency={currency}
                />
              )}
            </React.Fragment>
          )
        })}
      </ReportCardLayout>
    </ReportViewport>
  )
}

const RecapCopy = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing.md}rem;
  flex-wrap: wrap;
  line-height: 1.5;
`
